import { BaseCard } from '@/components/uiKit/Cards/BaseCard';
import React, { useEffect, useRef, useMemo } from 'react';
import { BaseTooltip } from '../Tooltip';
import { Counter } from '../Counter';
import { AssetActionsBar } from '@/components/pages/AssetsPreviewPage';
import { FaultReasons } from '@/modules';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import { healthStatusFlagsToString } from '@/utils/shared';
import { SparklesIcon, Lightbulb, CheckSquare } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ObservationsDrawer, VisibilityConfig } from './ObservationsDrawer';
import { ActionIcon } from '../ActionIcon';
import { useActiveAssetsType } from '@/hooks/shared/useActiveAssetsType';

interface ComponentCardProps {
  item: any;
  isSelected: boolean;
  showHealthStatus?: boolean;
  onClick?: () => void;
  variant?: 'base' | 'maintenance';
  visibilityConfig?: VisibilityConfig;
  actionIconVariant?: 'minimal' | 'badge';
  badgePosition?: 'top-right' | 'bottom-left';
}
// @TODO: Seperate to smaller components + create a single componet with variant for asset selector and components selector(!!!)
export const ComponentCard = ({
  item,
  onClick,
  isSelected,
  showHealthStatus = true,
  visibilityConfig = { observations: true, actions: true },
  actionIconVariant = 'badge',
  badgePosition = 'bottom-left',
}: ComponentCardProps) => {
  const isCustomAnalysis = item?.id?.includes('custom-part-analysis');
  const cardRef = useRef<HTMLDivElement>(null);
  const { selectedDiagnostic } = useSelector((state: RootState) => state.assetsStudio);
  const { isMobileAsset } = useActiveAssetsType();

  const componentData = useMemo(
    () => selectedDiagnostic?.[1]?.assets?.[0]?.components?.find((el: any) => el.component_id === item.component_id),
    [selectedDiagnostic, item.component_id]
  );

  const currentDiagnosticHealthStatus =
    componentData?.health_status || item?.subcomponent_health_status || item.health_status;

  // Extract diagnostics for this component
  const diagnostics = useMemo(() => {
    // Try multiple sources for diagnostics data
    return componentData?.diagnostics || item?.diagnostics || [];
  }, [componentData, item]);

  // Calculate total observations count - using evidences_text array
  const observationsCount = useMemo(() => {
    // Check for evidences_text array (pre-extracted observations)
    const evidencesText = componentData?.evidences_text || item?.evidences_text || [];
    if (evidencesText.length > 0) {
      return evidencesText.length;
    }

    // Fallback: extract from diagnostics sources_info
    const count = diagnostics.reduce((total: number, diagnostic: any) => {
      const evidenceCount = (diagnostic.sources_info || []).filter(
        (source: any) => source.evidence_text && source.evidence_text.trim()
      ).length;
      return total + evidenceCount;
    }, 0);

    return count;
  }, [componentData, item, diagnostics]);

  // Calculate total actions count
  const actionsCount = useMemo(() => {
    const actionsDescriptions = componentData?.actions_descriptions || item?.actions_descriptions || [];
    return actionsDescriptions.length;
  }, [componentData, item]);

  useEffect(() => {
    if (isSelected && cardRef.current) {
      setTimeout(() => {
        cardRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }, 100);
    }
  }, [isSelected]);
  return (
    <BaseTooltip
      className="min-w-full max-w-[100px]"
      content={`${item.name} ${
        item.isNotMonitored
          ? '- N/A'
          : showHealthStatus && currentDiagnosticHealthStatus
          ? '- ' + (currentDiagnosticHealthStatus === 'na' ? 'N/A' : currentDiagnosticHealthStatus)
          : ''
      }`}
    >
      <BaseCard
        ref={cardRef}
        dataTestId={`${item.name}_card`}
        onClick={() => !item.disabled && onClick && onClick()}
        key={item.id}
        classNames={{
          card: `rounded-lg hover:ring-1 hover:ring-inset ring-primary-500 hover:cursor-pointer p-1 ${
            isSelected && 'ring-2 ring-inset'
          } ${
            isCustomAnalysis ? 'text-primary-600 dark:text-primary-400' : ''
          }  h-[4.3rem] min-h-[4.3rem] max-h-[4.5rem]  flex flex-col ${
            item?.id?.includes('all-') && 'h-[3rem] min-h-[3rem] max-h-[3rem] flex items-center'
          } ${item.disabled ? 'hover:cursor-not-allowed hover:ring-0 opacity-50' : ''}`,
          content: `rounded-md font-medium h-full w-full self-center text-sm flex flex-col ${
            currentDiagnosticHealthStatus === 'na' && 'bg-neutral-25 dark:bg-dark-bg-sidebar'
          } ${item?.id?.includes('all-') && 'min-h-[3rem] flex  items-center'}`,
        }}
      >
        <div className="flex flex-col h-full w-full py-1">
          {/* Top section: Component name and health status */}
          <div className="flex items-start justify-between w-full gap-1 min-w-0">
            <div className="flex flex-col text-left flex-1 min-w-0">
              <div
                className={cn(
                  'truncate text-ellipsis overflow-hidden whitespace-nowrap text-black dark:text-white min-w-0 text-sm max-w-full',
                  isCustomAnalysis ? 'text-primary-500 dark:text-primary-400' : ''
                )}
              >
                {/* {isCustomAnalysis && <SparklesIcon size={14} />} */}
                {item.name}
              </div>
              {/* {isCustomAnalysis && <div className="text-gray-500 font-normal text-sm ">PLACEHOLDER</div>} */}
              <div className="flex items-center gap-2">
                {currentDiagnosticHealthStatus &&
                  (showHealthStatus || (!showHealthStatus && currentDiagnosticHealthStatus === 'na')) && (
                    <div
                      data-testid={`${item.name}_health_status`}
                      className={`text-${currentDiagnosticHealthStatus} text-xs capitalize text-left py-0.5 gap-2 flex items-center font-semibold`}
                    >
                      {currentDiagnosticHealthStatus === 'na' ? 'N/A' : currentDiagnosticHealthStatus}
                    </div>
                  )}
                <FaultReasons
                  size="sm"
                  showReasons={true}
                  counterOnly={true}
                  reasons={
                    item.reasons_for_na
                      ? [...healthStatusFlagsToString(item), ...item.reasons_for_na]
                      : healthStatusFlagsToString(item)
                  }
                />
              </div>
            </div>
          </div>

          {/* Bottom section: Action icons in footer */}
          <div className="flex justify-end items-center gap-2 -mt-[10px] ml-auto pr-0 -translate-y-[5px]">
            {/* {visibilityConfig.observations && observationsCount > 0 && isMobileAsset && (
              <ObservationsDrawer
                componentName={item.name}
                diagnostics={diagnostics}
                evidencesText={componentData?.evidences_text || item?.evidences_text || []}
                actionsDescriptions={componentData?.actions_descriptions || item?.actions_descriptions || []}
                componentHealthStatus={currentDiagnosticHealthStatus}
                initialTab="observations"
                visibilityConfig={visibilityConfig}
              >
                <ActionIcon
                  variant={actionIconVariant}
                  badgePosition={badgePosition}
                  icon={<Lightbulb className="text-foreground hover:text-primary-500 transition-colors" />}
                  count={observationsCount}
                  tooltip={`View ${observationsCount} observation${observationsCount !== 1 ? 's' : ''}`}
                />
              </ObservationsDrawer>
            )} */}
            {visibilityConfig.actions && actionsCount > 0 && (
              <ObservationsDrawer
                componentName={item.name}
                diagnostics={diagnostics}
                evidencesText={componentData?.evidences_text || item?.evidences_text || []}
                actionsDescriptions={componentData?.actions_descriptions || item?.actions_descriptions || []}
                componentHealthStatus={currentDiagnosticHealthStatus}
                initialTab="actions"
                visibilityConfig={visibilityConfig}
              >
                <ActionIcon
                  variant={actionIconVariant}
                  badgePosition={badgePosition}
                  icon={<CheckSquare className="text-foreground hover:text-primary-500 transition-colors" />}
                  count={actionsCount}
                  tooltip={`View ${actionsCount} action${actionsCount !== 1 ? 's' : ''}`}
                />
              </ObservationsDrawer>
            )}
            {(item?.id?.includes('all-') || item.counter !== undefined) && !item.hideCounter ? (
              <div className="-translate-y-[5px]">
                <Counter value={item?.counter} variant={'tab'} size="md" />
              </div>
            ) : (
              item && <AssetActionsBar asset={item} variant="component-card" onClick={onClick} badgePosition={badgePosition} actionIconVariant={actionIconVariant} />
            )}
          </div>
        </div>
      </BaseCard>
    </BaseTooltip>
  );
};
